import { PaginationProps, PaginationReturn } from '@common/types/service';
import {
    IAddUserSubmitRequest,
    ICompany,
    ICompanySchema,
    IEmployee,
    IPartnership,
    IPartnershipAnswer,
    IResultRequestsManagement,
    Role,
} from '@company/types/company';
import { api } from '@services/api';
import { getFilterString, getPaginationString } from '@utils/service';

interface CompanyTypesAllowed {
    label: string;
    value: number;
    allowed: boolean;
    reason: string;
}

const companyTypesApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getCompanyTypesAllowed: builder.query<CompanyTypesAllowed[], void>({
            query: () => '/company/type/user-allowed/',
            providesTags: ['CompanyTypesAllowed'],
            transformResponse: (response: { name: string; id: number; allowed: boolean; reason: string }[]) =>
                response.map((companyType) => ({
                    label: companyType.name,
                    value: companyType.id,
                    allowed: companyType.allowed,
                    reason: companyType.reason,
                })),
        }),
        getUserCompanies: builder.query<PaginationReturn<ICompany> | ICompany[], Partial<PaginationProps>>({
            query: ({ filters, ...pagination }) =>
                `company/${pagination || filters ? '?' : ''}${pagination ? getPaginationString(pagination) : ''}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: ['UserCompanies'],
        }),
        getManagementUserCompanies: builder.query<PaginationReturn<ICompany> | ICompany[], Partial<PaginationProps>>({
            query: ({ filters, ...pagination }) =>
                `management/company/${pagination || filters ? '?' : ''}${
                    pagination ? getPaginationString(pagination) : ''
                }${filters ? getFilterString(filters) : ''}`,
            providesTags: ['ManagementUserCompanies'],
        }),
        getCompanyUsers: builder.query<
            PaginationReturn<IEmployee> | IEmployee[],
            PaginationProps & { company_id: string }
        >({
            query: ({ company_id, filters, ...pagination }) =>
                `company/${company_id}/user-profile/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_result, _err, { company_id }) => [{ type: 'CompanyUsers', id: company_id }],
        }),
        getManagementCompanyUsers: builder.query<
            PaginationReturn<IEmployee> | IEmployee[],
            PaginationProps & { company_id: string }
        >({
            query: ({ company_id, filters, ...pagination }) =>
                `management/company/${company_id}/user-profile/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_result, _err, { company_id }) => [{ type: 'ManagementCompanyUsers', id: company_id }],
        }),
        getCompanyUserRole: builder.query<IEmployee, { company_id: string }>({
            query: ({ company_id }) => `company/${company_id}/user-profile/me/`,
            providesTags: (_res, _err, { company_id }) => [{ type: 'UserCompanyRole', id: company_id }],
        }),
        deleteUserFromCompany: builder.mutation<void, { company_id: string; user_id: string }>({
            query: ({ company_id, user_id }) => ({
                url: `company/${company_id}/user-profile/${user_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { company_id }) => [
                'UserCompanies',
                { type: 'UserCompanyRole', id: company_id },
                { type: 'ManagementCompanyUsers', id: company_id },
            ],
        }),
        addUserToCompanyRequest: builder.mutation<
            IAddUserSubmitRequest,
            { company_id: string; inviteeName: string; inviteeEmail: string }
        >({
            query: ({ company_id, inviteeEmail, inviteeName }) => ({
                url: `company/${company_id}/invite/`,
                method: 'POST',
                body: {
                    invitee_name: inviteeName,
                    invitee_email: inviteeEmail,
                },
            }),
            invalidatesTags: ['UsersCompaniesInvites'],
        }),
        deleteUsersFromCompany: builder.mutation<void, { company_id: string; usersSelected: string[] }>({
            query: ({ company_id, usersSelected }) => {
                return {
                    url: `company/${company_id}/user-profile/?ids=${usersSelected.join(',')}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: (_res, _err, { company_id }) => [
                'UserCompanies',
                { type: 'CompanyUsers', id: company_id },
                'ManagementUserCompanies',
                { type: 'ManagementCompanyUsers', id: company_id },
            ],
        }),
        deleteManagementUsersFromCompany: builder.mutation<void, { company_id: string; usersSelected: string[] }>({
            query: ({ company_id, usersSelected }) => {
                return {
                    url: `management/company/${company_id}/user-profile/?ids=${usersSelected.join(',')}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: (_res, _err, { company_id }) => [
                'UserCompanies',
                { type: 'CompanyUsers', id: company_id },
                'ManagementUserCompanies',
                { type: 'ManagementCompanyUsers', id: company_id },
            ],
        }),
        getCompanyUserRoles: builder.query<Role[], void>({
            query: () => 'public/company/user/role/',
            providesTags: ['UserCompanyRoles'],
        }),
        updateUserRole: builder.mutation<void, { company_id: string; user_id: string; role_id: number }>({
            query: ({ company_id, user_id, role_id }) => ({
                url: `company/${company_id}/user-profile/${user_id}/role/`,
                method: 'PATCH',
                body: {
                    role_id: role_id,
                },
            }),
            invalidatesTags: (_res, _err, { company_id }) => [
                { type: 'CompanyUsers', id: company_id },
                { type: 'ManagementCompanyUsers', id: company_id },
            ],
        }),
        deleteCompanyOfUser: builder.mutation<void, { company_id: string }>({
            query: ({ company_id }) => ({
                url: `management/company/${company_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ManagementUserCompanies', 'UserCompanies'],
        }),
        getCompanyTypes: builder.query<{ id: number; name: string }[], void>({
            query: () => 'public/company/type/',
            providesTags: ['CompanyTypes'],
        }),
        getCompaniesRequests: builder.query<
            PaginationReturn<IResultRequestsManagement> | IResultRequestsManagement[],
            PaginationProps
        >({
            query: ({ filters, ...pagination }) =>
                `management/company/request?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: ['CompanyRequests'],
        }),
        patchCompanyRequest: builder.mutation<void, { company_id: number; accept: boolean }>({
            query: ({ company_id, accept }) => ({
                url: `management/company/request/${company_id}/answer/`,
                method: 'PATCH',
                body: { accept },
            }),
            invalidatesTags: ['CompanyRequests'],
        }),
        addCompanyImage: builder.mutation<{ id: number; file: string; type: 'rich_text' }, { file: File }>({
            query: ({ file }) => {
                const formData = new FormData();
                formData.append('type', 'rich_text');
                formData.append('file', file);

                return {
                    url: 'company/images/',
                    method: 'POST',
                    body: formData,
                };
            },
        }),
        createManagementCompany: builder.mutation<
            ICompanySchema,
            Partial<ICompanySchema> & {
                commit?: boolean;
            }
        >({
            query: ({ commit, ...body }) => ({
                url: `management/company/${commit !== undefined ? `?commit=${commit}` : ''}`,
                method: 'POST',
                body: {
                    ...body,
                    support_emails: body.support_emails ? [body.support_emails] : [],
                },
            }),
            invalidatesTags: ['Companies', 'UserCompanies'],
        }),
        requestNewCompany: builder.mutation<
            { id: number; company: { id: number; name: string } },
            Partial<
                Omit<ICompanySchema, 'services' | 'name'> & {
                    services_ids: number[];
                    company_name: string;
                    commit?: boolean;
                }
            >
        >({
            query: ({ commit, ...body }) => ({
                url: `company/request/new-company/${commit !== undefined ? `?commit=${commit}` : ''}`,
                method: 'POST',
                body: {
                    ...body,
                    support_emails: body.support_emails ? [body.support_emails] : [],
                },
            }),
            invalidatesTags: ['Companies', 'UserCompanies'],
        }),
        getCompanyPartnerships: builder.query<
            PaginationReturn<IPartnership> | IPartnership[],
            PaginationProps & { company_id: string }
        >({
            query: ({ company_id, filters, ...pagination }) =>
                `company/${company_id}/partnership/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_res, _err, { company_id }) => [{ type: 'CompanyPartnerships', id: company_id }],
        }),
        /**
         * WARNING: DO NOT send multiple company id's in the array as it will create a single partnership with multiples companies (Future proofing).
         *
         * For now, you want to send an individual request to each company provided.
         */
        createPartnershipRequest: builder.mutation<IPartnership, { company_id: string; to_companies_ids: string[] }>({
            query: ({ company_id, ...body }) => ({
                url: `company/${company_id}/request/partnership/`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, { company_id, to_companies_ids }) => [
                { type: 'CompanyPartnerships', id: company_id },
                ...to_companies_ids.map((toCompanyID) => ({ type: 'CompanyPartnerships', id: toCompanyID })),
                { type: 'AvailableCompaniesToPartner', id: company_id },
                ...to_companies_ids.map((toCompanyID) => ({ type: 'AvailableCompaniesToPartner', id: toCompanyID })),
            ],
        }),
        deleteMultiplePartnerships: builder.mutation<void, { company_id: string; partnership_ids: number[] }>({
            query: ({ company_id, partnership_ids }) => ({
                url: `company/${company_id}/partnership/?${partnership_ids
                    .map((partnershipID) => `id=${partnershipID}`)
                    .join('&')}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CompanyPartnerships', 'AvailableCompaniesToPartner'], // Since there are multiple companies, unknwon ids, involved invalidate all
        }),
        deletePartnership: builder.mutation<
            void,
            { company_id: string; partnership_id: number; other_company_id: string }
        >({
            query: ({ company_id, partnership_id }) => ({
                url: `company/${company_id}/partnership/${partnership_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { company_id, other_company_id }) => [
                { type: 'CompanyPartnerships', id: company_id },
                { type: 'CompanyPartnerships', id: other_company_id },
                { type: 'AvailableCompaniesToPartner', id: company_id },
                { type: 'AvailableCompaniesToPartner', id: other_company_id },
            ],
        }),
        answerPartnershipRequest: builder.mutation<
            IPartnershipAnswer,
            { company_id: string; request_id: number; accept: boolean; to_company_id: string }
        >({
            query: ({ company_id, request_id, ...body }) => ({
                url: `company/${company_id}/request/${request_id}/answer/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_res, _err, { company_id, to_company_id }) => [
                { type: 'CompanyPartnerships', id: company_id },
                { type: 'CompanyPartnerships', id: to_company_id },
            ],
        }),
        getAvailableCompaniesToPartner: builder.query<Pick<ICompany, 'id' | 'name'>[], { company_id: string }>({
            query: ({ company_id }) => `company/${company_id}/request/partnership/available-companies/`,
            providesTags: (_res, _err, { company_id }) => [{ type: 'AvailableCompaniesToPartner', id: company_id }],
        }),
    }),
});

export const {
    useGetCompanyTypesAllowedQuery,
    useGetUserCompaniesQuery,
    useGetCompanyUsersQuery,
    useDeleteUserFromCompanyMutation,
    useAddUserToCompanyRequestMutation,
    useDeleteUsersFromCompanyMutation,
    useDeleteManagementUsersFromCompanyMutation,
    useGetCompanyUserRoleQuery,
    useGetCompanyUserRolesQuery,
    useUpdateUserRoleMutation,
    useGetManagementUserCompaniesQuery,
    useDeleteCompanyOfUserMutation,
    useGetCompanyTypesQuery,
    useGetCompaniesRequestsQuery,
    usePatchCompanyRequestMutation,
    useGetManagementCompanyUsersQuery,
    useAddCompanyImageMutation,
    useCreateManagementCompanyMutation,
    useRequestNewCompanyMutation,
    useGetCompanyPartnershipsQuery,
    useCreatePartnershipRequestMutation,
    useDeletePartnershipMutation,
    useAnswerPartnershipRequestMutation,
    useDeleteMultiplePartnershipsMutation,
    useGetAvailableCompaniesToPartnerQuery,
} = companyTypesApi;
export default companyTypesApi;
